import { useState, useRef, useCallback, useEffect } from 'react';
import {
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  ITrainerSubmit,
} from '@/types/trainers';

import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';
import { SelectedFeature } from '@/types/scout';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import { Input } from '@/components/shadcn/input';
import { clearSelectedFeaturesMap } from '../map/layers/Coverage';
import TrainerHeader from './TrainerHeader';
import TrainerFooter from './TrainerFooter';
import Feedback from './shared/Feedback';
import ErrorMessage from './shared/ErrorMessage';
import TrainerController, {
  TrainerButtons,
  TrainerInputs,
} from './TrainerController';
import TrainerButton from './TrainerButton';
import RegionPickerTrainerContent from './content/RegionPickerTrainerContent';

interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
}

function mergeIntervals(intervals: [number, number][]): [number, number][] {
  if (intervals.length === 0) {
    return [];
  }

  // Sort intervals by their start value
  intervals.sort((a, b) => a[0] - b[0]);

  const merged: [number, number][] = [];
  let current = intervals[0];

  for (let i = 1; i < intervals.length; i++) {
    const [currentStart, currentEnd] = current;
    const [nextStart, nextEnd] = intervals[i];

    if (currentEnd >= nextStart) {
      current = [currentStart, Math.max(currentEnd, nextEnd)];
    } else {
      merged.push(current);
      current = intervals[i];
    }
  }

  merged.push(current);
  return merged;
}

function processSelectedSegments(segments: SelectedFeature[]) {
  const segmentIdRegionMap: Record<string, [number, number][]> = {};

  segments.map(segment => {
    const { id } = segment;
    const split = String(id).split('-');
    const segmentId = split[0];
    let sectionStr = split[split.length - 1];

    if (!segmentId) {
      console.error('Encountered invalid segment id or region');
      return;
    }

    if (!sectionStr) {
      // If no segment section, then assume its the whole segment
      sectionStr = '[0, 1]';
    }

    const section = JSON.parse(sectionStr);
    segmentIdRegionMap[segmentId] ||= [];
    segmentIdRegionMap[segmentId].push(section);
  });

  Object.keys(segmentIdRegionMap).forEach(segmentId => {
    const regions = segmentIdRegionMap[segmentId];
    const mergedRegions = mergeIntervals(regions);
    segmentIdRegionMap[segmentId] = mergedRegions;
  });

  return segmentIdRegionMap;
}

export default function RegionPickerTrainer({
  data: initialData,
  page,
  name,
  description,
  location,
  tool,
}: Props) {
  const headerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [isBenchmark, setIsBenchmark] = useState(false);
  const [data, setData] = useState<ITrainersData | undefined>(initialData);
  const { feedback, setFeedback } = useTrainerFeedback();
  const [selectedSegments, setSelectedSegments] = useState<SelectedFeature[]>(
    [],
  );
  const [inputError, setInputError] = useState('');
  const [lanes, setLanes] = useState('');

  const { incrementReviews } = useTrainerReviews();

  const {
    leaseId = 'noleaseid',
    frames,
    closeFrames,
    images: contentImages = [],
  } = data?.content || {};

  const clearAllData = () => {
    setIsBenchmark(false);
    setData(undefined);
    setInputError('');
    setLanes('');
    if (tool?.queue === 'eventRegionPicker') {
      clearSelectedFeaturesMap();
      setSelectedSegments([]);
    }
  };

  function validateLaneInput(): {
    formatted: string;
    valid: boolean;
  } {
    if (lanes === 'na') {
      return {
        formatted: 'na',
        valid: true,
      };
    }

    if (lanes === '') {
      setInputError('Please provide lane indices');
      return {
        formatted: '',
        valid: false,
      };
    } else {
      try {
        const split = lanes.split(',');
        const laneIndices = split
          .filter(i => i !== '')
          .map(index => {
            const trimmed = index.trim();
            const match = trimmed.match(/^(D|AD)(\d+)$/i);
            if (!match) return NaN;
            return parseInt(match[2], 10);
          });

        if (laneIndices.some(i => isNaN(i))) {
          setInputError(
            'Invalid lane indices provided. Provide comma separated numbers with a D or AD (indicating drive or against drive directions, respectively) prefix.',
          );
          return {
            formatted: lanes,
            valid: false,
          };
        }

        if (laneIndices.some(i => i < 0 || i > 10)) {
          setInputError(
            'Invalid lane indices provided. Provide comma separated numbers with a D or AD (indicating drive or against drive directions, respectively) prefix.',
          );
          return {
            formatted: lanes,
            valid: false,
          };
        }

        const sortedLaneIndices = split.map(i => i.trim()).sort();
        setLanes(sortedLaneIndices.join(', '));
        setInputError('');

        return {
          formatted: sortedLaneIndices.join(', '),
          valid: true,
        };
      } catch (error) {
        setInputError(
          'Invalid lane indices provided. Provide comma separated numbers with a D or AD (indicating drive or against drive directions, respectively) prefix.',
        );
        return {
          formatted: lanes,
          valid: false,
        };
      }
    }
  }

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onSubmit = async ({ outcome, value }: ITrainerSubmit) => {
    if (!leaseId) {
      return;
    }

    const finalValue = outcome ?? value;
    const submission = {
      leaseId,
      outcome: finalValue,
      value:
        tool?.queue === 'eventRegionPicker'
          ? processSelectedSegments(selectedSegments)
          : finalValue,
    };

    if (isBenchmark) {
      await submitBenchmark(toolId, submission);
    }

    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      submission,
    );

    clearAllData();

    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }
    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };

  return (
    <div className="flex h-[90%] flex-col">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <>
          <RegionPickerTrainerContent
            data={data}
            tool={tool}
            setSelectedSegments={setSelectedSegments}
          />
          {tool?.widget === 'eventLanesImpacted' && (
            <div>
              <div className="my-2 flex flex-col items-center">
                <Input
                  className="w-1/5"
                  placeholder={'Enter comma separated lane indices'}
                  value={lanes}
                  type="text"
                  onInput={(e: any) => setLanes(e.target.value)}
                />
                {inputError !== '' && (
                  <span className="text-cinnabarDanger-600">{inputError}</span>
                )}
              </div>
            </div>
          )}
          <TrainerController
            containerRef={controllerRef}
            onSubmit={onSubmit}
            data={data}
            tool={tool}
            isDataReady={
              Boolean(data?.content) &&
              (selectedSegments.length > 0 ||
                tool?.queue !== 'eventRegionPicker')
            }>
            <TrainerButtons />
            {/* TODO: Add validation to ensure that comma separated integers are provided only 
                and also process to sort before sending to the backend
            */}
            {tool?.widget === 'eventLanesImpacted' && (
              <>
                <TrainerButton
                  label="Not Sure"
                  onClick={() => {
                    onSubmit({ annotations: [], outcome: 'skip', value: '' });
                  }}
                  type={'button'}
                />
                <TrainerButton
                  label="Submit"
                  onClick={() => {
                    const { valid, formatted } = validateLaneInput();
                    if (valid) {
                      onSubmit({
                        annotations: [],
                        value: formatted,
                      });
                    }
                  }}
                  disabled={lanes === ''}
                  type={'button'}
                />
              </>
            )}
            {tool?.queue === 'eventRegionPicker' && (
              <>
                <TrainerButton
                  label="Not Sure"
                  onClick={() => {
                    onSubmit({ annotations: [], outcome: 'skip', value: '' });
                  }}
                  type={'button'}
                />
                <TrainerButton
                  label="Not a construction scene"
                  onClick={() => {
                    onSubmit({
                      annotations: [],
                      outcome: 'badData',
                      value: '',
                    });
                  }}
                  type={'button'}
                />
              </>
            )}
          </TrainerController>
        </>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
}
